import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ArrowUpDown, Lock, Crosshair } from "lucide-react";
import { fmtN, PaceArrow } from "@/components/overview/Momentum";
import { TH, StageCells, GroupHead, SubHead } from "@/components/overview/StageCells";
import { METRIC_GROUPS, fmtFor, fmtBay } from "./SvcBits";

const LEFT1 = "sticky left-0 z-[1] w-[180px] min-w-[180px] max-w-[180px] lg:w-[230px] lg:min-w-[230px] lg:max-w-[230px]";
const LEFT2 = "lg:sticky lg:left-[230px] lg:z-[1]";
const DESKTOP_DEFAULT = ["labour", "parts", "ccs", "gs_ros", "total_gs"];
const NARROW_DEFAULT = ["gs_ros", "total_gs"];
const FIXED = 8;

function Bay({ v, rank, needs, testId }) {
  if (v == null) return <span className={`text-[10px] ${needs ? "text-amber-700" : "text-muted-foreground"}`} title={needs ? "Bays not captured in Admin › Dealers" : "No bays (1S) or no activity"} data-testid={testId}>{needs ? "set bays" : "—"}</span>;
  return <span className="num" data-testid={testId}><b>{fmtBay(v)}</b>{rank && <sup className="text-[9px] text-muted-foreground ml-0.5">{rank}</sup>}</span>;
}

function Groups({ r, groups, period, bg, prefix }) {
  return <>{groups.map(([k]) => <StageCells key={k} c={r.cells?.[k]} period={period} retail={k === "total_gs"} sticky={k === "total_gs"} bg={bg} fmt={fmtFor(k)} testPrefix={`${prefix}-${k}`} />)}</>;
}

function AggRow({ r, label, groups, period, testId, cls, bg }) {
  return (
    <tr className={`${cls} ${bg}`} data-testid={testId}>
      <td className={`px-2 py-1.5 ${LEFT1} ${bg} whitespace-nowrap overflow-hidden text-ellipsis`}>{label} <span className="text-muted-foreground font-normal text-[10px]">· {r.workshops} workshops · {r.with_target} with targets</span></td>
      <td className={`px-2 py-1.5 ${LEFT2} ${bg}`} />
      <td className="px-2 py-1.5" />
      <td className="px-2 py-1.5 text-right num">{r.projected_gs == null ? "—" : `₹${fmtFor("total_gs")(r.projected_gs)}`}</td>
      <td className="px-2 py-1.5 text-center"><PaceArrow m={r.momentum} compact /></td>
      <td className="px-2 py-1.5 text-right num">{r.rev_per_ro == null ? "—" : `₹${fmtN(r.rev_per_ro)}`}</td>
      <td className="px-2 py-1.5 text-right"><Bay v={r.gs_per_bay_day} testId={`${testId}-gs-bay`} /></td>
      <td className="px-2 py-1.5 text-right"><Bay v={r.bp_per_bay_day} testId={`${testId}-bp-bay`} /></td>
      <Groups r={r} groups={groups} period={period} bg={bg} prefix={testId} />
    </tr>
  );
}

export function WorkshopTable({ rows, zones, total, zoneLabels, isManager, period, exWarranty, onFocus }) {
  const [visible, setVisible] = useState(() => (typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches ? DESKTOP_DEFAULT : NARROW_DEFAULT));
  const [sort, setSort] = useState({ key: "rank", dir: 1 });
  const [collapsed, setCollapsed] = useState({});
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const h = (e) => setVisible(e.matches ? DESKTOP_DEFAULT : NARROW_DEFAULT);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  const groups = METRIC_GROUPS.filter(([k]) => visible.includes(k));
  const toggleGroup = (k) => setVisible((v) => (v.includes(k) ? (v.length > 1 ? v.filter((x) => x !== k) : v) : METRIC_GROUPS.map(([s]) => s).filter((s) => s === k || v.includes(s))));
  const zoneMap = useMemo(() => Object.fromEntries((zones || []).map((z) => [z.label, z])), [zones]);
  const isWeek = period?.kind === "week";
  const projLabel = isWeek ? (period.current ? "Proj. week-end GS" : "Proj. GS") : "Proj. GS";
  const paceLabel = isWeek ? "DoD" : "5-day";
  const grouped = zoneLabels.length > 1;

  const sorter = (a, b) => {
    const v = (r) => {
      if (sort.key === "rank") return r.rank == null ? 9999 : r.rank;
      if (sort.key === "workshop") return r.code;
      if (sort.key === "projected") return r.projected_gs ?? -1;
      if (sort.key === "per_ro") return r.rev_per_ro ?? -1;
      if (sort.key === "gs_bay") return r.gs_per_bay_day ?? -1;
      if (sort.key === "bp_bay") return r.bp_per_bay_day ?? -1;
      if (sort.key === "momentum") return r.momentum?.change ?? -1e9;
      const [k, f] = sort.key.split(".");
      return r.cells?.[k]?.[f] ?? -1;
    };
    const x = v(a), y = v(b);
    return (x > y ? 1 : x < y ? -1 : 0) * sort.dir;
  };
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: key === "rank" || key === "workshop" ? 1 : -1 }));
  const Th = ({ k, children, className = "" }) => (
    <th className={`${TH} ${className} cursor-pointer select-none hover:text-foreground`} onClick={() => toggleSort(k)} data-testid={`sov-sort-${k.replace(".", "-")}`}>
      <span className="inline-flex items-center gap-0.5">{children}{sort.key === k && <ArrowUpDown size={10} />}</span>
    </th>
  );
  const blocks = (grouped ? zoneLabels : ["All"]).map((label) => ({ label, rows: rows.filter((r) => !grouped || r.zone === label).sort(sorter) })).filter((g) => g.rows.length);
  const colCount = FIXED + groups.length * 4;
  const [planLbl, tdLbl] = isWeek ? ["Week Target", "WTD Target"] : ["Target", "MTD Target"];

  return (
    <div className="border bg-card rounded-md" data-testid="sov-workshop-table">
      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-3">
        <h3 className="font-heading text-sm font-semibold">C · Workshops <span className="text-muted-foreground font-normal text-xs">· {planLbl} / {tdLbl} / Ach / % (Ach ÷ {tdLbl}) · ranked on Total GS % · bays = ROs per bay per active day{exWarranty ? " · ex-warranty" : ""}</span></h3>
        <div className="ml-auto flex flex-wrap items-center gap-1" data-testid="sov-group-chips">
          {METRIC_GROUPS.map(([k, l]) => (
            <button key={k} onClick={() => toggleGroup(k)} className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${visible.includes(k) ? "bg-foreground text-background border-foreground" : "text-muted-foreground hover:bg-secondary"}`} aria-pressed={visible.includes(k)} data-testid={`sov-group-chip-${k}`}>{l}</button>
          ))}
        </div>
        {isManager && <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground"><Lock size={11} /> other workshops show rank only</span>}
      </div>
      <div className="overflow-auto max-h-[560px]">
        <table className="w-full text-xs" style={{ minWidth: 680 + groups.length * 288 }}>
          <thead className="border-b sticky top-0 bg-card z-[2]">
            <tr>
              <Th k="workshop" className={`text-left ${LEFT1} bg-card z-[3]`}>Workshop</Th>
              <th className={`${TH} ${LEFT2} bg-card`}>Zone</th>
              <Th k="rank" className="text-right">Rank</Th>
              <Th k="projected" className="text-right">{projLabel}</Th>
              <Th k="momentum" className="text-center">{paceLabel}</Th>
              <Th k="per_ro" className="text-right">₹ / GS RO</Th>
              <Th k="gs_bay" className="text-right">GS ROs/bay/day</Th>
              <Th k="bp_bay" className="text-right">BP ROs/bay/day</Th>
              {groups.map(([k, l]) => <GroupHead key={k} label={l} sticky={k === "total_gs"} testId={`sov-workshop-group-${k}`} />)}
            </tr>
            <tr className="text-[9px] text-muted-foreground">
              <th className={`${LEFT1} bg-card`} /><th className={`${LEFT2} bg-card`} /><th /><th /><th /><th /><th /><th />
              {groups.map(([k]) => <SubHead key={k} period={period} sticky={k === "total_gs"} />)}
            </tr>
          </thead>
          <tbody>
            {blocks.map((g) => {
              const sub = zoneMap[g.label];
              const isCollapsed = !!collapsed[g.label];
              return [
                grouped && !isManager && (
                  <tr key={`${g.label}-head`} className="bg-secondary/60 cursor-pointer hover:bg-secondary" onClick={() => setCollapsed((c) => ({ ...c, [g.label]: !c[g.label] }))} data-testid={`sov-zone-head-${g.label.replace(/\s+/g, "_")}`}>
                    <td className="px-2 py-1 font-semibold text-[11px] whitespace-nowrap" colSpan={colCount}><span className="inline-flex items-center gap-1 sticky left-2">{isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />} {g.label} <span className="text-muted-foreground font-normal">· {g.rows.length} workshops</span></span></td>
                  </tr>
                ),
                !isCollapsed && g.rows.map((r) => (
                  <tr key={r.code} className={`border-b last:border-0 bg-card hover:bg-muted/50 ${r.masked ? "text-muted-foreground" : ""}`} data-testid={`sov-workshop-${r.code}`}>
                    <td className={`px-2 py-1 ${LEFT1} bg-card whitespace-nowrap overflow-hidden text-ellipsis`}>
                      {r.masked ? <span className="inline-flex items-center gap-1"><Lock size={10} /> {r.code} <span className="text-[10px]">{r.name}</span></span> : (
                        <span className="inline-flex items-center gap-1">
                          <Link to={`/service/workshop/${r.code}`} className="font-medium hover:underline" data-testid={`sov-workshop-link-${r.code}`}>{r.code} <span className="text-muted-foreground font-normal">{r.name}</span></Link>
                          {onFocus && <button onClick={() => onFocus(r.code)} className="text-muted-foreground hover:text-foreground" title="Open this workshop in the overview" data-testid={`sov-workshop-focus-${r.code}`}><Crosshair size={11} /></button>}
                        </span>
                      )}
                      {!r.masked && r.has_target === false && <span className="ml-1.5 rounded bg-amber-50 text-amber-800 border border-amber-200 px-1 py-0.5 text-[9px]" data-testid={`sov-workshop-notarget-${r.code}`}>no target</span>}
                    </td>
                    <td className={`px-2 py-1 ${LEFT2} bg-card text-muted-foreground whitespace-nowrap text-[10px]`}>{r.zone}</td>
                    <td className="px-2 py-1 text-right num font-bold" data-testid={`sov-workshop-${r.code}-rank`}>{r.rank ?? "—"}</td>
                    {r.masked ? <td className="px-2 py-1 text-center text-[10px] text-muted-foreground" colSpan={FIXED - 3 + groups.length * 4}>— masked · rank only —</td> : (
                      <>
                        <td className="px-2 py-1 text-right num" data-testid={`sov-workshop-${r.code}-proj`}>{r.projected_gs == null ? "—" : `₹${fmtFor("total_gs")(r.projected_gs)}`}</td>
                        <td className="px-2 py-1 text-center"><PaceArrow m={r.momentum} compact testId={`sov-workshop-${r.code}-pace`} /></td>
                        <td className="px-2 py-1 text-right num" data-testid={`sov-workshop-${r.code}-perro`}>{r.rev_per_ro == null ? "—" : <>₹{fmtN(r.rev_per_ro)}{r.rev_per_ro_rank && <sup className="text-[9px] text-muted-foreground ml-0.5">{r.rev_per_ro_rank}</sup>}</>}</td>
                        <td className="px-2 py-1 text-right"><Bay v={r.gs_per_bay_day} rank={r.gs_bay_rank} needs={r.needs_bays} testId={`sov-workshop-${r.code}-gs-bay`} /></td>
                        <td className="px-2 py-1 text-right"><Bay v={r.bp_per_bay_day} rank={r.bp_bay_rank} needs={r.needs_bays} testId={`sov-workshop-${r.code}-bp-bay`} /></td>
                        <Groups r={r} groups={groups} period={period} bg="bg-card" prefix={`sov-workshop-${r.code}`} />
                      </>
                    )}
                  </tr>
                )),
                !isManager && sub && grouped && <AggRow key={`${g.label}-sub`} r={sub} label={`${g.label} subtotal`} groups={groups} period={period} testId={`sov-zone-${g.label.replace(/\s+/g, "_")}`} cls="font-semibold border-b" bg="bg-secondary" />,
              ];
            })}
            {total && <AggRow r={total} label={total.label} groups={groups} period={period} testId="sov-total" cls="font-bold border-t-2 border-foreground/30" bg="bg-secondary" />}
            {rows.length === 0 && <tr><td colSpan={colCount} className="px-3 py-6 text-center text-muted-foreground">No workshops in this selection</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
